import { apiUrl } from '../Utility/ApiUrl'
import { debounce } from '../Utility/Debounce'
import { getElement } from '../Utility/Dom'
import { SWAP_EVENT } from '../Utility/SwapReinit'

// VHW-147 — replaces the submit-button-driven filter flow on the result page
// with an inline AJAX swap. Every checkbox toggle, applied-chip removal and
// search submit fetches a fresh fragment from `/api/course-search/results`,
// swaps the two `data-swap` regions, and announces the new total count via a
// polite live region — no full page reload, no „pending vs. active" filter state.
//
// Autocomplete-driven text input is intentionally NOT live: typing fires no
// AJAX, only the submit/Enter does. Autocomplete itself keeps using its own
// suggest endpoint.

const DEBOUNCE_DELAY = 250
const ENDPOINT = '/api/course-search/results'
const PLUGIN_PREFIX = 'tx_sitepackage_coursesearch'
const SEARCH_OPEN = 'is-search-open'
const PREFIX_PATTERN = new RegExp(`^${PLUGIN_PREFIX}\\[(filter|q|removeFilter|page|sort)\\](.*)$`)

interface ApplyOptions {
  extra?: Record<string, string>
}

interface FragmentResponse {
  fragments?: {
    filter?: string
    results?: string
  }
  totalCount?: number
  url?: string
}

class CourseFilterLive {
  private currentAbort: AbortController | null = null
  private form: HTMLFormElement | null

  constructor() {
    this.form = document.querySelector<HTMLFormElement>('[data-js="search-filter-form"]')
  }

  public init(): void {
    if (!this.form) return
    if (!document.querySelector('[data-swap="results"]')) return

    this.form.addEventListener('submit', (event): void => {
      this.handleSubmit(event)
    })

    const update = debounce((): void => {
      void this.applyFilters()
    }, DEBOUNCE_DELAY)

    // Listen on `document`, not the form: the sort radios live in the result
    // region (outside the form DOM) and associate via their `form` attribute,
    // so their change event never bubbles to the form. `target.form` resolves
    // the association for both in-form filters and the detached sort control.
    document.addEventListener('change', (event): void => {
      const target = event.target as HTMLInputElement | null
      if (!(target instanceof HTMLInputElement)) return
      if (target.type !== 'checkbox' && target.type !== 'radio') return
      if (target.form !== this.form) return
      update()
    })

    // Enter-toggles-checkbox lives in CourseFilterToggle so it also works on
    // the hero (which does not load CourseFilterLive). The change it fires is
    // picked up by the `change` listener above.

    // "Häufig gesucht" and "Letzte Suche" chips are plain <a> links that would
    // cause a full page reload. Intercept them here so they fill the q input
    // and trigger the AJAX live filter instead.
    document.addEventListener('click', (event): void => {
      const target = event.target as Element | null
      const chip = target?.closest<HTMLAnchorElement>('[data-js="frequent-chip"], [data-js="last-searched-chip"]')
      if (!chip || !this.form) return

      const url = new URL(chip.href)
      const q = url.searchParams.get(`${PLUGIN_PREFIX}[q]`) ?? ''
      const input = this.form.querySelector<HTMLInputElement>(`[name="${PLUGIN_PREFIX}[q]"]`)
      if (!input) return

      event.preventDefault()
      input.value = q
      this.form.requestSubmit()
    })
  }

  private announce(totalCount: number | undefined): void {
    if (typeof totalCount !== 'number') return
    const announcer = document.querySelector<HTMLElement>('[data-js="course-search-announcer"]')
    if (!announcer) return
    announcer.textContent = `${totalCount} Fortbildungen gefunden`
  }

  private async applyFilters(options: ApplyOptions = {}): Promise<void> {
    if (!this.form) return

    const resultsContainer = document.querySelector<HTMLElement>('[data-swap="results"]')
    const filterContainer = document.querySelector<HTMLElement>('[data-swap="filter"]')
    if (!resultsContainer || !filterContainer) return

    const openTriggerTarget = filterContainer
      .querySelector<HTMLElement>('[data-js="search-filter-trigger"][aria-expanded="true"]')
      ?.getAttribute('data-target') ?? null
    const focusSelector = this.focusSelector(document.activeElement)
    const keywordFocused = document.activeElement instanceof HTMLInputElement
      && document.activeElement.type === 'search'

    if (this.currentAbort) this.currentAbort.abort()
    this.currentAbort = new AbortController()

    resultsContainer.setAttribute('aria-busy', 'true')

    const url = this.buildUrl(options.extra ?? {})

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
        signal: this.currentAbort.signal,
      })

      if (!response.ok) {
        resultsContainer.setAttribute('aria-busy', 'false')
        return
      }

      const data = await response.json() as FragmentResponse
      if (keywordFocused) {
        this.pinKeywordRow()
      }

      const newResults = this.swap(resultsContainer, data.fragments?.results)
      const newFilter = this.swap(filterContainer, data.fragments?.filter)
      if (!newResults || !newFilter) {
        resultsContainer.setAttribute('aria-busy', 'false')
        return
      }

      if (typeof data.url === 'string' && data.url !== '') {
        history.replaceState({}, '', data.url)
      }

      document.dispatchEvent(new CustomEvent(SWAP_EVENT))

      this.restoreState(openTriggerTarget, focusSelector)
      this.announce(data.totalCount)
    } catch (error) {
      if ((error as Error).name === 'AbortError') return
      resultsContainer.setAttribute('aria-busy', 'false')
    }
  }

  private buildUrl(extra: Record<string, string>): string {
    const params = new URLSearchParams()

    if (this.form) {
      new FormData(this.form).forEach((value, key): void => {
        const match = PREFIX_PATTERN.exec(key)
        if (!match) return
        const [, group, rest] = match
        if (group === 'removeFilter') return
        params.append(`${group}${rest}`, value.toString())
      })
    }

    Object.entries(extra).forEach(([key, value]): void => {
      const match = PREFIX_PATTERN.exec(key)
      if (!match) return
      const [, group, rest] = match
      params.append(`${group}${rest}`, value)
    })

    return `${apiUrl(ENDPOINT)}?${params.toString()}`
  }

  private escape(value: string): string {
    return value.replace(/(["\\])/g, '\\$1')
  }

  private focusSelector(element: Element | null): null | string {
    if (!(element instanceof HTMLInputElement)) return null
    if (!element.name) return null
    if (element.type === 'checkbox' || element.type === 'radio') {
      return `input[name="${this.escape(element.name)}"][value="${this.escape(element.value)}"]`
    }
    if (element.type === 'search') {
      return `input[name="${this.escape(element.name)}"]`
    }
    return null
  }

  private handleSubmit(event: Event): void {
    event.preventDefault()
    if (!this.form) return

    const submitter = (event as SubmitEvent).submitter ?? null

    if (submitter instanceof HTMLButtonElement && submitter.classList.contains('search-filter__applied-remove') && submitter.name) {
      void this.applyFilters({ extra: { [submitter.name]: submitter.value } })
      return
    }

    void this.applyFilters()
  }

  // The swap destroys the very keyword field the user is typing in, so
  // `:focus-within` — the guard keeping that field expanded inside an
  // already-collapsed sticky header (CourseSearch.scss) — drops out mid-submit.
  // Its replacement is then inserted collapsed, i.e. `visibility: hidden`, and
  // `focus()` no-ops on hidden elements: `restoreState()` below never gets the
  // focus back and the header snaps 88px shorter as the results arrive.
  //
  // The header sits outside the swapped region and therefore keeps its classes,
  // so pinning the row open there bridges that gap — the new field is inserted
  // visible, focus lands on it, and `:focus-within` takes over again before the
  // class comes off.
  private pinKeywordRow(): void {
    const header = getElement('search-page-header')
    if (header === null) return

    header.classList.add(SEARCH_OPEN)
    // Swap, `history.replaceState`, the swap event and `restoreState()` all run
    // synchronously in this task, so the next frame is both the earliest moment
    // focus is guaranteed to be restored and still early enough that no frame
    // ever paints the collapsed row.
    requestAnimationFrame((): void => {
      header.classList.remove(SEARCH_OPEN)
    })
  }

  private restoreState(openTriggerTarget: null | string, focusSelector: null | string): void {
    if (openTriggerTarget) {
      const trigger = document.querySelector<HTMLButtonElement>(
        `[data-js="search-filter-trigger"][data-target="${openTriggerTarget}"]`
      )
      // Use detail:1 (mouse-click simulation) so CourseFilterToggle does NOT call
      // focusFirstInteractive() — that path is reserved for real keyboard activation
      // (detail=0) and would cause an unwanted scroll jump after every AJAX swap.
      trigger?.dispatchEvent(new MouseEvent('click', { bubbles: true, detail: 1 }))
    }

    if (focusSelector) {
      const focusTarget = document.querySelector<HTMLElement>(focusSelector)
      // preventScroll: the element was already visible before the swap; restoring
      // focus must not re-scroll the page to its position.
      focusTarget?.focus({ preventScroll: true })
    }
  }

  private swap(oldNode: HTMLElement, html: string | undefined): HTMLElement | null {
    if (typeof html !== 'string' || html.trim() === '') return null

    const template = document.createElement('template')
    template.innerHTML = html.trim()
    const newNode = template.content.firstElementChild
    if (!(newNode instanceof HTMLElement)) return null

    oldNode.replaceWith(newNode)
    return newNode
  }
}

const courseFilterLive = new CourseFilterLive()
courseFilterLive.init()
